import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Outlet, useNavigate } from 'react-router';

import { Button, Icon } from '@ds/core';
import { Breadcrumb, Sidebar, Topbar } from '@ds/navigation';
import type { Presenca, SidebarEntry, Tema } from '@ds/navigation';
import { useAviso } from '@/comuns/toast';
import { EVENTO_DO_LOGO } from '@/comuns/logoDaClinica';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { InadimplenciaDaClinica, MinhaConta } from '@/data/tipos';
import { AvisoDeInadimplencia } from '@/comuns/AvisoDeInadimplencia';
import { AvisoDeTeste } from '@/comuns/AvisoDeTeste';
import type { TesteDaClinica } from '@/comuns/AvisoDeTeste';
import { PainelDeNotificacoes } from './PainelDeNotificacoes';
import { AVISOS_DA_VOLTA } from '@/data/agendaGoogle';
import { EditarContaModal, SenhaModal } from './ContaModais';
import { useRotaAtual } from '@/router/useRotaAtual';
import { rotaPorId, rotas } from '@/router/rotas';
import { podeNaRota } from '@/router/guardas';
import type { Rota, SecaoDeNavegacao, Subrota } from '@/router/rotas';
import { atual, encerrarImpersonacao, impersonacaoAtual, moduloLigado, rotuloPapel, sair, trocarClinica } from '@/session/sessao';
import { PilulaDoAtendimento } from '@/comuns/PilulaDoAtendimento';
import { PainelDoBrain } from '@/screens/brain/PainelDoBrain';
import { aquecerMicrofone, destravarAudio } from '@/screens/brain/voz/useConversaPorVoz';
import { SeletorDeUnidade } from '@/comuns/SeletorDeUnidade';
import { CampoDaBuscaGlobal, PaletaGlobal } from '@/comuns/BuscaGlobal';
import { NovidadesDaVersaoModal } from '@/comuns/NovidadesDaVersaoModal';
import { VERSAO_DO_PRODUTO } from '@/data/novidades';
import { abrirSuporte } from '@/comuns/suporte';
import { aplicarTema, observarSistema, temaSalvo } from '@/tema';

/* Importadas como módulo, não como caminho em texto: o Vite versiona o arquivo
   e o build passa a levar a imagem junto. A variante escura vai no DOM ao lado
   da clara e quem escolhe é o CSS (ver `tokens/base.css`). */
/* O menu é Deep Clinic nos dois temas: a logo é sempre a de fundo escuro, e o modo
   recolhido usa o monograma FM do próprio Sidebar. */
import logoSobreEscuro from '@assets/formulamed-logo-branco.png';

export interface ShellDoProdutoProps {
    nav: SecaoDeNavegacao[];
}

/**
 * Abaixo desta largura de conteúdo a tela é "estreita": respiro lateral de
 * 16 px em vez de 24, e as telas de duas colunas empilham.
 *
 * Mede-se o `<main>`, não a janela: com o menu aberto, 1024 px de janela deixam
 * menos espaço que 768 px com o menu recolhido.
 */
const LARGURA_ESTREITA = 640;

/**
 * O que o `<main>` estreito passa às telas. Telas de duas colunas leem
 * `var(--layout-colunas, <suas colunas>)`: largo, vale o valor delas; estreito,
 * uma coluna só. Variável herdada, e não medida em cada tela, para que as
 * dezenas de grades não reimplementem cada uma o seu ResizeObserver.
 */
const MAIN_ESTREITO = { padding: 16, '--respiro-do-main': '16px', '--layout-colunas': 'minmax(0,1fr)' } as CSSProperties;

/** O respiro largo do `<main>`, também como variável: a tela cheia do Brain o anula. */
const MAIN_LARGO = { padding: 24, '--respiro-do-main': '24px' } as CSSProperties;

/**
 * Moldura das duas surfaces em sessão: rail de módulos, topbar e o conteúdo.
 *
 * Clínica e plataforma diferem no conjunto de módulos e na logo, não no
 * comportamento — duas cópias divergiriam na primeira correção de foco.
 */
export function ShellDoProduto({ nav }: ShellDoProdutoProps) {
    const navigate = useNavigate();
    const handle = useRotaAtual();
    const sessao = atual();
    const [variante, setVariante] = useState<'rail' | 'expanded'>('rail');
    const [tema, setTema] = useState<Tema>(() => temaSalvo());
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const [conta, setConta] = useState<MinhaConta | null>(null);
    const [editandoConta, setEditandoConta] = useState(false);
    const [trocandoSenha, setTrocandoSenha] = useState(false);
    const [novidadesAbertas, setNovidadesAbertas] = useState(false);
    const [paletaAberta, setPaletaAberta] = useState(false);
    const [brainAberto, setBrainAberto] = useState(false);
    const [principal, setPrincipal] = useState<HTMLElement | null>(null);
    const [estreito, setEstreito] = useState(false);
    const daPlataforma = sessao?.perfil === 'plataforma';

    /* Ref por estado: o efeito roda quando o `<main>` existe, seja qual for o
       caminho de render que o montou. `clientWidth` inclui o padding, então
       trocar 24 por 16 não muda a medida — não há vaivém. */
    useEffect(() => {
        if (!principal || typeof ResizeObserver === 'undefined') return undefined;
        const medir = () => setEstreito(principal.clientWidth < LARGURA_ESTREITA);
        medir();
        const ro = new ResizeObserver(medir);
        ro.observe(principal);
        return () => ro.disconnect();
    }, [principal]);
    const emailDaSessao = sessao?.email;
    const clinicaDaSessao = sessao?.clinicaId;
    const [logoDaClinica, setLogoDaClinica] = useState<string | null>(null);

    /* O logotipo da clínica no topo (0098). É marca, não dado: se a leitura
       falhar, fica o ícone padrão — e a aba de Configurações, que lê de novo,
       mostra o erro a quem for trocar. Relê quando a aba avisa que mudou. */
    useEffect(() => {
        if (!clinicaDaSessao || daPlataforma) { setLogoDaClinica(null); return undefined; }
        let vivo = true;
        const ler = () => {
            dados().logoDaClinica()
                .then((url) => { if (vivo) setLogoDaClinica(url); })
                .catch(() => { if (vivo) setLogoDaClinica(null); });
        };
        ler();
        window.addEventListener(EVENTO_DO_LOGO, ler);
        return () => { vivo = false; window.removeEventListener(EVENTO_DO_LOGO, ler); };
    }, [clinicaDaSessao, daPlataforma]);

    /* A fatura vencida da clínica (T9): aviso desde o 1º dia, só leitura aos 15.
       Relê ao voltar para a aba — é por onde a pessoa chega depois de pagar no
       gateway. A faixa é aviso, não guarda: se a leitura falhar ela some, e a aba
       Assinatura, que lê de novo, mostra o erro; quem recusa a escrita é o banco. */
    const [inadimplencia, setInadimplencia] = useState<InadimplenciaDaClinica | null>(null);
    /* O teste do autocadastro (0150) vem junto, pelo mesmo motivo: a faixa acompanha a
       volta à aba — é por onde a pessoa chega depois de cancelar ou trocar o cartão. */
    const [teste, setTeste] = useState<TesteDaClinica | null>(null);
    useEffect(() => {
        if (!clinicaDaSessao || daPlataforma) { setInadimplencia(null); setTeste(null); return undefined; }
        let vivo = true;
        const ler = () => {
            dados().minhaInadimplencia()
                .then((i) => { if (vivo) setInadimplencia(i); })
                .catch(() => { if (vivo) setInadimplencia(null); });
            dados().meuTeste()
                .then((t) => { if (vivo) setTeste(t); })
                .catch(() => { if (vivo) setTeste(null); });
        };
        ler();
        window.addEventListener('focus', ler);
        return () => { vivo = false; window.removeEventListener('focus', ler); };
    }, [clinicaDaSessao, daPlataforma]);

    /* O sininho (0151): conta a cada minuto e ao voltar à aba — sem tempo real, que não
       leva o cabeçalho da clínica ativa. A lista só é lida quando o painel abre.
       `releitura` força a contagem depois de marcar como lido. */
    const [naoLidas, setNaoLidas] = useState(0);
    const [notificacoesAbertas, setNotificacoesAbertas] = useState(false);
    const [releitura, setReleitura] = useState(0);
    useEffect(() => {
        if (!clinicaDaSessao || daPlataforma) { setNaoLidas(0); return undefined; }
        let vivo = true;
        const contar = () => {
            dados().contarNotificacoes()
                .then((n) => { if (vivo) setNaoLidas(n); })
                .catch(() => undefined);
        };
        contar();
        const relogio = window.setInterval(contar, 60_000);
        window.addEventListener('focus', contar);
        return () => { vivo = false; window.clearInterval(relogio); window.removeEventListener('focus', contar); };
    }, [clinicaDaSessao, daPlataforma, releitura]);

    /* A volta do Google Agenda (0152) chega com `?google-agenda=<resultado>`: diz o que
       aconteceu e tira o parâmetro. É o parâmetro sair que impede o aviso de repetir —
       se o efeito rodar de novo, já não há o que ler. */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const resultado = params.get('google-agenda');
        if (!resultado) return;
        const volta = AVISOS_DA_VOLTA[resultado] ?? AVISOS_DA_VOLTA.erro;
        if (volta) aviso({ title: volta.title, description: volta.description, ...(volta.tone ? { tone: volta.tone } : {}) });
        params.delete('google-agenda');
        const resto = params.toString();
        navigate(`${window.location.pathname}${resto ? `?${resto}` : ''}`, { replace: true });
    }, [aviso, navigate]);

    useEffect(() => {
        aplicarTema(tema);
        return observarSistema(tema, () => aplicarTema(tema));
    }, [tema]);

    /* A conta na clínica: nome, telefone e o status que a pessoa escolheu.
       Operador da plataforma não tem cadastro de clínica, nem status aqui. Se a
       leitura falhar, o menu segue com o nome da sessão — e "Editar conta", que
       lê de novo, mostra o erro a quem abrir. */
    useEffect(() => {
        if (!emailDaSessao || daPlataforma) return;
        let vivo = true;
        dados().minhaConta()
            .then((c) => { if (vivo) setConta(c); })
            .catch(() => { if (vivo) setConta(null); });
        return () => { vivo = false; };
    }, [emailDaSessao, daPlataforma]);

    /* O status mudava na tela e em lugar nenhum. */
    const trocarPresenca = async (presenca: Presenca) => {
        try {
            setConta(await dados().salvarMinhaPresenca(presenca));
        } catch (e) {
            aviso({ title: 'Não foi possível trocar o status', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const pedirSaida = async () => {
        const ok = await confirmar({
            titulo: 'Sair da Fórmula MED?',
            descricao: 'Para voltar, é preciso entrar de novo com e-mail e senha.',
            confirmar: 'Sair',
        });
        if (!ok) return;
        await sair();
        navigate('/entrar', { replace: true });
    };

    const idsDaNav = nav.flatMap((s) => s.items);
    const moduloAtual = handle ? moduloDaRota(handle.rota, idsDaNav) : undefined;
    /* O orbe do Brain, no lugar do antigo copiloto: só na clínica, com o módulo
       ligado, e não na própria rota do Brain. Abre a VecchIA cobrindo a janela inteira. */
    const brainNoTopo = !daPlataforma && moduloLigado('brain') && handle?.rota.id !== 'brain';

    const items: SidebarEntry[] = nav.flatMap((secao) => [
        { section: secao.section },
        ...secao.items.flatMap((id) => {
            const rota = rotaPorId(id);
            if (!rota) return [];
            /* Módulo desligado some do menu. A guarda continua respondendo 404
               para quem digitar o endereço: esconder é experiência, quem
               recusa é a rota. */
            if (!moduloLigado(id)) return [];
            /* O mesmo critério das rotas irmãs: módulo cuja guarda nega a
               permissão some do menu. Era só o módulo desligado — e o
               profissional via "Financeiro" para clicar e receber 403. */
            if (!permitidaNoMenu(rota)) return [];
            return [{
                id: rota.id,
                label: rota.label,
                icon: rota.icon,
                sub: [...rota.children.map((c) => ({ id: c.path, label: c.label })), ...irmasNoMenu(rota)],
            }];
        }),
    ]);

    const crumbs = migalhas(handle?.rota, handle?.subrota, moduloAtual);
    /* Numa rota irmã (Fichas, por exemplo) não há subrota: o item aceso é o
       último trecho do próprio caminho. Sem isto o menu abre o módulo certo e
       não marca nada dentro dele. */
    const subAtiva = handle?.subrota?.path
        ?? (handle?.rota.navPai ? ultimoTrecho(handle.rota.path) : undefined);

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--surface-secondary)' }}>
            <Sidebar
                items={items} variant={variante}
                activeId={moduloAtual?.id} activeSubId={subAtiva}
                onToggleVariant={() => setVariante((v) => (v === 'rail' ? 'expanded' : 'rail'))}
                onSelect={(id) => { const r = rotaPorId(id); if (r) navigate(r.path); }}
                onSelectSub={(subId, moduloId) => { const r = rotaPorId(moduloId); if (r) navigate(`${r.path}/${subId}`); }}
                logoSrc={logoSobreEscuro}
            />

            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <FaixaDeImpersonacao />
                {inadimplencia && (
                    <AvisoDeInadimplencia inadimplencia={inadimplencia} onPagar={() => navigate('/app/configuracoes/assinatura')} />
                )}
                {teste && !inadimplencia && (
                    <AvisoDeTeste teste={teste} onVer={() => navigate('/app/configuracoes/assinatura')} />
                )}
                <Topbar
                    breadcrumb={<Breadcrumb items={crumbs} onNavigate={(id) => { const r = rotaPorId(id); if (r) navigate(r.path); }} />}
                    search={<CampoDaBuscaGlobal onAbrir={() => setPaletaAberta(true)} />}
                    onSearch={() => setPaletaAberta(true)}
                    onAssistant={brainNoTopo ? () => {
                        /* Ainda dentro do toque: no celular a VecchIA abre falando, e o áudio só destrava com gesto. */
                        if (!brainAberto && window.matchMedia?.('(max-width: 600px)').matches) {
                            destravarAudio();
                            /* E o microfone, pela mesma razão: quem abre a conversa por voz é
                               um efeito, que roda depois do toque — e o Safari do iPhone trata
                               o pedido fora do gesto de outro jeito (22/09/2026). */
                            aquecerMicrofone();
                        }
                        setBrainAberto((v) => !v);
                    } : undefined}
                    assistantLabel="Abrir a VecchIA"
                    assistantExpanded={brainAberto}
                    /* A central é da clínica: operador da plataforma não tem avisos aqui. */
                    onNotifications={daPlataforma ? undefined : () => setNotificacoesAbertas((v) => !v)}
                    notificationsCount={naoLidas}
                    /* O suporte é para a clínica: o operador da plataforma é quem atende. */
                    onHelp={daPlataforma ? undefined : () => abrirSuporte(VERSAO_DO_PRODUTO)}
                    clinic={sessao?.clinica ?? undefined}
                    clinicLogo={logoDaClinica ?? undefined}
                    actions={(
                        <>
                            {/* Logo depois da clínica: recorta agenda, atendimentos e contas. */}
                            <SeletorDeUnidade />
                            {/* A consulta aberta segue a pessoa por todas as telas: é
                                por ela que se volta ao editor sem procurar na lista. */}
                            <PilulaDoAtendimento />
                        </>
                    )}
                    account={sessao ? {
                        user: conta?.nome ?? sessao.nome,
                        email: sessao.email,
                        role: rotuloPapel(sessao.papel),
                        /* Pelo id quando há: é ele que o banco usa para mostrar só a
                           clínica escolhida. A demonstração só tem nomes. */
                        contas: sessao.vinculos
                            ? sessao.vinculos.map((v) => ({ id: v.clinicaId, nome: v.clinica, atual: v.clinicaId === sessao.clinicaId }))
                            : sessao.clinicas.map((nome) => ({ id: nome, nome, atual: nome === sessao.clinica })),
                        tema,
                        onTemaChange: setTema,
                        versao: VERSAO_DO_PRODUTO,
                        presenca: conta?.presenca ?? 'online',
                        onPresencaChange: (p) => void trocarPresenca(p),
                        onEditarConta: () => setEditandoConta(true),
                        onSelect: (id) => {
                            if (id === 'seguranca') setTrocandoSenha(true);
                            if (id === 'notificacoes') navigate('/app/configuracoes/notificacoes');
                            if (id === 'novidades') setNovidadesAbertas(true);
                            if (id === 'ajuda') abrirSuporte(VERSAO_DO_PRODUTO);
                        },
                        /* Abaixo de 760 px a topbar tira o botão Ajuda contando com esta entrada. */
                        extra: daPlataforma ? undefined : [{ id: 'ajuda', label: 'Ajuda pelo WhatsApp', icon: 'circle-help' }],
                        ocultar: daPlataforma ? ['presenca', 'editar', 'notificacoes'] : undefined,
                        /* Trocar de clínica não mexe no caminho: o tenant vem da
                           sessão, então a mesma URL passa a mostrar outra
                           clínica — e é justamente isso que se quer. */
                        onTrocarConta: (escolhida) => { trocarClinica(escolhida.nome); navigate(0); },
                        onSair: () => void pedirSaida(),
                    } : undefined}
                />

                <main ref={setPrincipal} style={{ flex: 1, minWidth: 0, overflow: 'auto', ...MAIN_LARGO, ...(estreito ? MAIN_ESTREITO : null) }}>
                    <Outlet />
                </main>
            </div>

            <EditarContaModal open={editandoConta} onClose={() => setEditandoConta(false)} onSalva={setConta} />
            <SenhaModal open={trocandoSenha} onClose={() => setTrocandoSenha(false)} />
            <PainelDeNotificacoes
                aberto={notificacoesAbertas}
                aoFechar={() => setNotificacoesAbertas(false)}
                aoMudar={() => setReleitura((n) => n + 1)}
                aoAbrir={(destino) => navigate(destino)}
            />
            {novidadesAbertas && <NovidadesDaVersaoModal onFechar={() => setNovidadesAbertas(false)} />}
            <PaletaGlobal nav={nav} aberta={paletaAberta} onMudar={setPaletaAberta} />
            {brainNoTopo && <PainelDoBrain aberto={brainAberto} aoFechar={() => setBrainAberto(false)} moduloAtual={moduloAtual?.id} />}
            {confirmacao}
        </div>
    );
}

/**
 * A faixa de quem está dentro de uma clínica que não é sua.
 *
 * Fica acima de tudo e não fecha: o risco de esquecer que se está numa clínica
 * alheia é justamente o que ela existe para impedir. O contador não é enfeite —
 * a impersonação expira em 30 minutos, e depois disso o banco simplesmente para
 * de responder.
 */
function FaixaDeImpersonacao() {
    const navigate = useNavigate();
    const imp = impersonacaoAtual();
    const [agora, setAgora] = useState(() => Date.now());

    useEffect(() => {
        if (!imp) return;
        const t = setInterval(() => setAgora(Date.now()), 30_000);
        return () => clearInterval(t);
    }, [imp]);

    if (!imp) return null;

    const restam = Math.max(0, Math.round((imp.expiraEm - agora) / 60000));

    return (
        <div
            role="status"
            style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '9px 18px', background: 'var(--warning-50, #FDF6E3)',
                borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-ui)', fontSize: 13,
                color: 'var(--text-primary)',
            }}
        >
            <Icon name="eye" size={15} />
            <span>
                Você está em <strong>{imp.clinica}</strong> como operador da plataforma —{' '}
                <strong>somente leitura</strong>, {restam} min restantes.
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>Motivo: {imp.motivo}</span>
            <span style={{ marginLeft: 'auto' }}>
                <Button
                    size="sm" variant="secondary"
                    onClick={async () => { await encerrarImpersonacao(); navigate('/admin', { replace: true }); }}
                >
                    Sair da clínica
                </Button>
            </span>
        </div>
    );
}

function ultimoTrecho(caminho: string): string {
    return caminho.split('/').filter(Boolean).slice(-1)[0] ?? '';
}

/**
 * As rotas irmãs que pedem lugar no menu deste módulo.
 *
 * O item some para quem não tem a permissão da rota: oferecer um link que
 * responde 403 é pior do que não oferecê-lo. Esconder é experiência — quem
 * autoriza é o servidor, e a guarda da rota continua lá.
 */
function irmasNoMenu(modulo: Rota) {
    return rotas
        .filter((r) => r.navPai === modulo.id)
        .filter(permitidaNoMenu)
        .map((r) => ({ id: ultimoTrecho(r.path), label: r.label }));
}

/** Toda guarda `perm:` da rota concedida à sessão. */
function permitidaNoMenu(rota: Rota): boolean {
    return rota.guards.every((g) => !g.startsWith('perm:') || podeNaRota(g.slice('perm:'.length)));
}

/**
 * O módulo que a navegação deve marcar. Telas de detalhe não estão na lista
 * lateral — `/app/contatos/:contatoId` acende "Contatos", e não coisa nenhuma.
 */
function moduloDaRota(rota: Rota, idsDaNav: string[]): Rota | undefined {
    if (idsDaNav.includes(rota.id)) return rota;
    return idsDaNav
        .map((id) => rotaPorId(id))
        .filter((r): r is Rota => !!r && rota.path.startsWith(`${r.path}/`))
        /* Mais específico primeiro: /app/atendimentos ganha de /app. */
        .sort((a, b) => b.path.length - a.path.length)[0];
}

function migalhas(rota: Rota | undefined, subrota: Subrota | undefined, modulo: Rota | undefined) {
    if (!rota) return [];
    const trilha = [];
    if (modulo && modulo.id !== rota.id) trilha.push({ id: modulo.id, label: modulo.label });
    trilha.push({ id: rota.id, label: rota.label });
    if (subrota) trilha.push({ id: `${rota.id}/${subrota.path}`, label: subrota.label });
    return trilha;
}
